/**
 * ПУБЛИЧНЫЙ КЛИЕНТ ДЛЯ API (без админских прав)
 */
import {
  API_BASE,
  buildQuery,
  pickAllowed,
  firstOk,
  normalizeListPayload,
  normalizeOnePayload,
  normalizeMediaItem,
  api,
} from './base';
import i18n from '../i18n';

/** Подмешиваем текущий язык из i18n, если явно не передан */
const withLocale = (params = {}) => {
  const lng = (i18n && i18n.language) ? i18n.language : 'ru';
  return { ...params, locale: params.locale ?? lng };
};

/* ===================== КЛИЕНТСКИЕ API (Favorites / Leads) ===================== */
export const FavoritesAPI = {
  list: () => api('/favorites'),
  add: (propertyId) =>
    api('/favorites', { method: 'POST', body: { property_id: propertyId } }),
  remove: (propertyId) => api(`/favorites/${propertyId}`, { method: 'DELETE' }),
};

/**
 * Нормализуем пользовательские поля формы в серверные:
 * name -> first_name, comment -> message, + трекинг.
 */
function normalizeLeadPayload(raw = {}) {
  const {
    type,
    name,
    first_name,
    last_name,
    phone,
    email,
    comment,
    message,

    // опциональные для sell_or_rent
    property_type,
    deal_type,
    region_id,
    district_id,
    price_min,
    price_max,
    area_min,
    area_max,
    bedrooms,

    // трекинг
    source_page,
    utm_source,
    utm_medium,
    utm_campaign,
    device_id,

    // согласия
    consent_privacy,
    consent_marketing,
  } = raw;

  const payload = {
    type, // важно — бэкенд читает ДО валидации
    first_name: (first_name || name || '').trim() || undefined,
    last_name: last_name || undefined,
    phone,
    email: email || undefined,
    message: (message || comment || '').trim() || undefined,

    // специфично для sell_or_rent
    property_type,
    deal_type,
    region_id,
    district_id,
    price_min,
    price_max,
    area_min,
    area_max,
    bedrooms,

    // трекинг
    source_page:
      source_page ||
      (typeof window !== 'undefined' ? window.location.pathname : undefined),
    utm_source:
      utm_source ||
      (typeof window !== 'undefined' ? localStorage.getItem('utm_source') : undefined) ||
      undefined,
    utm_medium:
      utm_medium ||
      (typeof window !== 'undefined' ? localStorage.getItem('utm_medium') : undefined) ||
      undefined,
    utm_campaign:
      utm_campaign ||
      (typeof window !== 'undefined' ? localStorage.getItem('utm_campaign') : undefined) ||
      undefined,
    device_id:
      device_id ||
      (typeof window !== 'undefined' ? localStorage.getItem('device_id') : undefined) ||
      undefined,

    // согласия по умолчанию
    consent_privacy:
      typeof consent_privacy === 'boolean' ? consent_privacy : true,
    consent_marketing:
      typeof consent_marketing === 'boolean' ? consent_marketing : false,
  };

  // подчистим undefined
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  return payload;
}

async function postUnifiedLead(body) {
  return api('/leads', { method: 'POST', body });
}

export const LeadsAPI = {
  // Базовый метод
  create: (payload) => postUnifiedLead(normalizeLeadPayload(payload)),

  // Удобный алиас
  submit: ({ type, ...rest }) =>
    postUnifiedLead(normalizeLeadPayload({ type, ...rest })),

  // Хелперы под страницы услуг
  sellOrRentService: (form) =>
    postUnifiedLead(normalizeLeadPayload({ type: 'sell_or_rent', ...form })),
  investmentService: (form) =>
    postUnifiedLead(normalizeLeadPayload({ type: 'investment', ...form })),
  consultingService: (form) =>
    postUnifiedLead(normalizeLeadPayload({ type: 'consulting', ...form })),
  designService: (form) =>
    postUnifiedLead(normalizeLeadPayload({ type: 'design', ...form })),
  analyticsService: (form) =>
    postUnifiedLead(normalizeLeadPayload({ type: 'analytics', ...form })),
};

/* ===================== ПУБЛИЧНЫЕ ЭНДПОИНТЫ ===================== */

export const PublicAPI = {
  health: () => api('/health'),
  config: (params = {}) => api(`/config/public${buildQuery(withLocale(params))}`),
  propertyTypes: (params = {}) => api(`/property-types${buildQuery(withLocale(params))}`),
  dealTypes: (params = {}) => api(`/deal-types${buildQuery(withLocale(params))}`),
  i18n: (locale) => api(`/i18n/${encodeURIComponent(locale)}`),
  navigation: (params = {}) => api(`/navigation${buildQuery(withLocale(params))}`),

  /* ===== Домашние секции / контент для главной ===== */
  homeSections: (params = {}) => api(`/home/sections${buildQuery(withLocale(params))}`),
  homeHero:     (params = {}) => api(`/home/hero${buildQuery(withLocale(params))}`),

  recommendations: (params = {}) => api(`/home/recommendations${buildQuery(withLocale(params))}`),
  investments:     (params = {}) => api(`/home/investments${buildQuery(withLocale(params))}`),
  objectsPopular:  (params = {}) => api(`/home/objects-popular${buildQuery(withLocale(params))}`),

  reviews: (params = {}) => api(`/reviews${buildQuery(withLocale(params))}`),

  /* ===== Награды ===== */
  awards: (params = {}) => api(`/awards${buildQuery(withLocale(params))}`),
  award: (id, params = {}) =>
    api(`/awards/${encodeURIComponent(id)}${buildQuery(withLocale(params))}`),

  /* ===== О НАС (about) ===== */
  aboutPosts: (params = {}) => api(`/about/posts${buildQuery(withLocale(params))}`),
  aboutPost:  (slug, params = {}) => api(`/about/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),

  /* ===== Контент ===== */
  blogPosts: (params = {}) => api(`/blog/posts${buildQuery(withLocale(params))}`),
  blogPost:  (slug, params = {}) => api(`/blog/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),

  newsPosts: (params = {}) => api(`/news/posts${buildQuery(withLocale(params))}`),
  newsPost:  (slug, params = {}) => api(`/news/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),

  socialMediaPosts: (params = {}) => api(`/socialmedia/posts${buildQuery(withLocale(params))}`),
  socialMediaPost: (slug, params = {}) =>
    api(`/socialmedia/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),

  /* ===== Гео / Поиск ===== */
  regions: (params = {}) => api(`/regions${buildQuery(withLocale(params))}`),

  districts: (params = {}) => api(`/districts${buildQuery(withLocale(params))}`),
  district:  (slug, params = {}) => api(`/districts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),
  districtProps: (slug, params = {}) =>
    api(`/districts/${encodeURIComponent(slug)}/properties${buildQuery(withLocale(pickAllowed(params)))}`),

  /* ===== Объекты недвижимости ===== */
  properties: (params = {}) =>
    api(`/properties${buildQuery(withLocale(pickAllowed(params)))}`),

  propertyBySlug: (slug, params = {}) =>
    api(`/properties/${encodeURIComponent(slug)}${buildQuery(withLocale(pickAllowed(params)))}`),

  // УДОБСТВА ОБЪЕКТА (public): GET /properties/{slug}/amenities?locale=ru|en
  propertyAmenities: (slugOrId, params = {}) =>
    api(`/properties/${encodeURIComponent(slugOrId)}/amenities${buildQuery(withLocale(params))}`),

  // ПЛАНЫ ОПЛАТЫ (public)
  propertyPaymentPlans: (slugOrId, params = {}) =>
    api(`/properties/${encodeURIComponent(slugOrId)}/payment-plans${buildQuery(withLocale(params))}`),

  propertyPaymentPlan: (slugOrId, planId, params = {}) =>
    api(`/properties/${encodeURIComponent(slugOrId)}/payment-plans/${encodeURIComponent(planId)}${buildQuery(withLocale(params))}`),

  // МЕНЕДЖЕРЫ ОБЪЕКТА (public)
  propertyManagers: (slugOrId, params = {}) =>
    api(`/properties/${encodeURIComponent(slugOrId)}/managers${buildQuery(withLocale(params))}`),

  search: (q, params = {}) =>
    api(`/properties/search${buildQuery(withLocale({ q, ...pickAllowed(params) }))}`),

  /* ===== Подборки ===== */
  // Список коллекций, с предпросмотром items, если include=items
  collections: (params = {}) => {
    const p = { ...withLocale(params) };
    if (p.with_items) {
      p.include = 'items';
      delete p.with_items;
    }
    return api(`/collections${buildQuery(p)}`);
  },
  collection: (slug, params = {}) =>
    api(`/collections/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),
  collectionDetail: ({ slug, ...params } = {}) =>
    api(`/collections/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`),

  /* ===== Авторские каталоги (PDF) ===== */
  catalogs: (params = {}) =>
    api(`/catalogs${buildQuery(withLocale(params))}`),

  /* ===== Универсальные помощники (пресса) ===== */
  pressList: (params = {}) =>
    firstOk([
      `/socialmedia/posts${buildQuery(withLocale(params))}`,
      `/news/posts${buildQuery(withLocale(params))}`,
      `/blog/posts${buildQuery(withLocale({ ...params, tag: 'media' }))}`,
    ]).then(normalizeListPayload),

  pressOne: (slug, params = {}) =>
    firstOk([
      `/socialmedia/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`,
      `/news/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`,
      `/blog/posts/${encodeURIComponent(slug)}${buildQuery(withLocale(params))}`,
    ]).then(normalizeOnePayload),

  /* ===== Юниты ===== */
  /** Юниты по объекту (список, с пагинацией/фильтрами) — передаём все юнит-параметры без фильтрации */
  propertyUnits: (slugOrId, params = {}) =>
    api(`/properties/${encodeURIComponent(slugOrId)}/units${buildQuery(withLocale(params))}`),

  /** Публичная карточка юнита */
  unit: (unitId, params = {}) =>
    api(`/units/${encodeURIComponent(unitId)}${buildQuery(withLocale(params))}`),

  /* ===== Менеджеры (PUBLIC, список всех) ===== */
  managers: (params = {}) =>
    api(`/managers${buildQuery(withLocale(params))}`),

  /* ===== Рекомендуемые объекты (FEATURED) ===== */
  /** GET /properties/featured?locale=ru&currency=AED&limit=12 */
  featuredProperties: (params = {}) =>
    api(`/properties/featured${buildQuery(withLocale(params))}`),

  /* ===== Инвестиционные объекты (INVESTMENT) ===== */
  /** GET /properties/investment?locale=ru&currency=AED&limit=12 */
  investmentProperties: (params = {}) =>
    api(`/properties/investment${buildQuery(withLocale(params))}`),

  /* ===== Объекты по ТИПУ СДЕЛКИ (АЛИАС) ===== */
  /**
   * GET /deal-types/{ident}/properties
   * ident: number | string (id | slug | exact name)
   * params: { locale?, currency?, page?, page_size? }
   */
  dealTypeProperties: (ident, params = {}) =>
    api(`/deal-types/${encodeURIComponent(ident)}/properties${buildQuery(withLocale(params))}`),

  /* ===== Корпоративный контент ===== */
  getCorporationContent: (params = {}) =>
    api(`/corporation${buildQuery(withLocale(params))}`),

  /* ===== Услуги ===== */
  services: (params = {}) => api(`/services${buildQuery(withLocale(params))}`),
};

const Public = {
  API_BASE,
  PublicAPI,
  FavoritesAPI,
  LeadsAPI,
  normalizeMediaItem,
  buildQuery,
};

export default Public;

